// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  useReactFlow,
  SelectionMode,
  MarkerType,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  type OnSelectionChangeParams,
  type FinalConnectionState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Boxes, Plus, Rocket, Trash2, Ungroup, X, Zap, Play, ChevronDown, ChevronUp } from "lucide-react";
import { remainingPct, uid, type WalletLedger } from "@/lib/algo/choreography";
import {
  LAUNCH_NODE_ID, defaultEdge, defaultSell, edgeLabel, walletLabels,
  type EdgeKind, type FlowGraph, type GraphEdge, type SellNode,
} from "@/lib/algo/graph";
import { PlaygroundConfigurator } from "./PlaygroundConfigurator";
import { canConnect, canSell, type Severity, type Validation } from "@/lib/algo/validate";
import { resolveDevId } from "@/lib/algo/fundNeeds";

const BUY_COLOR = "#10b981";    // emerald green
const ROTATE_COLOR = "#3b82f6"; // tailwind blue
const SELL_COLOR = "#ef4444";   // tailwind red
const SEV_COLOR: Record<Severity, string> = { ok: "", warn: "#14b8a6", block: "#ef4444" };
const LAUNCH_POS = { x: -260, y: 60 };

type WalletNodeData = { label: string; group: string; enabled: boolean; inRotation: boolean; orderLabel?: string; ledger?: WalletLedger; initBuy?: number; delay?: number };
type SellNodeData = { value: number; pct: number; severity: Severity };

function WalletNode({ data, selected }: NodeProps<Node<WalletNodeData>>) {
  const l = data.ledger;
  const rem = l ? remainingPct(l) : 0;
  return (
    <div className={`pg-node ${data.enabled ? "" : "off"} ${selected ? "sel" : ""} ${data.inRotation ? "rotated" : ""}`}>
      {data.orderLabel && <div className={`pg-num ${data.inRotation ? "sub" : ""}`}>{data.orderLabel}</div>}
      <Handle type="target" position={Position.Left} id="in" />
      <div className="pg-node-name">{data.label}</div>
      <div className="pg-node-grp">{data.group}</div>
      <div className="pg-node-stats">
        <span>{l ? l.cashSol.toFixed(2) : "0"} SOL</span>
        <span>{l ? compact(l.tokens) : "0"}</span>
        <span>{rem.toFixed(0)}%</span>
      </div>
      {(data.initBuy !== undefined || data.delay !== undefined) && (
        <div className="flex items-center justify-between mt-1 px-1 py-0.5 bg-black/40 rounded text-[9px] text-white/50 border border-white/5">
          <span>{data.initBuy !== undefined ? `${data.initBuy} SOL` : "-"}</span>
          <span>{data.delay !== undefined ? `${data.delay}s` : "-"}</span>
        </div>
      )}
      <div className="pg-bar"><i style={{ width: `${rem}%` }} /></div>
      <Handle type="source" position={Position.Right} id="buy" className="h-buy" title="BUY → drag to a wallet" />
      <Handle type="source" position={Position.Bottom} id="sell" className="h-sell" title="SELL → drag to empty (timed sell) or to a wallet (rotate)" />
    </div>
  );
}

type LaunchNodeData = { devLabel?: string; devBuy?: number; ledger?: WalletLedger };

function LaunchNode({ data }: NodeProps<Node<LaunchNodeData>>) {
  const l = data.ledger;
  return (
    <div className="pg-node launch">
      <div className="pg-num dev">DEV</div>
      <div className="pg-node-name"><Rocket size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />{data.devLabel ?? "Wallet 1"}</div>
      <div className="pg-node-grp">create + dev buy {data.devBuy ?? 0} SOL</div>
      {l && l.tokens > 0 && <div className="pg-launch-hold">{(l.tokens / 1e6).toFixed(2)}M tokens</div>}
      <Handle type="source" position={Position.Right} id="buy" className="h-buy" title="BUY → drag to a wallet" />
      <Handle type="source" position={Position.Bottom} id="sell" className="h-sell" title="SELL → drag to empty (sell the dev bag) or a wallet (rotate)" />
    </div>
  );
}

function SellNodeView({ data, selected }: NodeProps<Node<SellNodeData>>) {
  return (
    <div className={`pg-sell ${data.severity} ${selected ? "sel" : ""}`}>
      <Handle type="target" position={Position.Left} id="in" />
      <div className="pg-sell-vals">
        <span><b>{data.pct}%</b> sell</span>
        <span><b>{data.value}s</b> wait</span>
      </div>
    </div>
  );
}

const nodeTypes = { wallet: WalletNode, launch: LaunchNode, sell: SellNodeView };

function edgeToRf(e: GraphEdge, severity: Severity, selected: boolean): Edge {
  const base = e.kind === "buy" ? BUY_COLOR : ROTATE_COLOR;
  const stroke = SEV_COLOR[severity] || base;
  const dotted = e.kind === "rotate";
  return {
    id: e.id, source: e.source, target: e.target, sourceHandle: e.kind === "rotate" ? "sell" : "buy", targetHandle: "in", selected,
    type: "bezier",
    animated: severity === "block",
    label: `${severity === "block" ? "⛔ " : severity === "warn" ? "⚠ " : ""}${edgeLabel(e)}${e.bundleId ? " ⛓" : ""}`,
    labelStyle: { fill: severity === "ok" ? "#c6c6c6" : "#ffffff", fontSize: 10, fontWeight: 800 },
    labelBgStyle: { fill: severity === "ok" ? "#111111" : stroke, stroke, strokeWidth: 1 },
    labelBgPadding: [6, 4] as [number, number], labelBgBorderRadius: 6,
    style: { stroke, strokeWidth: e.bundleId ? 2.8 : e.kind === "buy" ? 2.4 : 1.8, strokeDasharray: dotted || severity === "block" ? "6 4" : undefined },
    markerEnd: { type: MarkerType.ArrowClosed, color: stroke },
    zIndex: 10,
  };
}

function sellEdge(s: SellNode, severity: Severity): Edge {
  const stroke = SEV_COLOR[severity] || SELL_COLOR;
  return {
    id: `se-${s.id}`, source: s.walletId, target: s.id, sourceHandle: "sell", targetHandle: "in",
    type: "bezier",
    style: { stroke, strokeWidth: 1.8, strokeDasharray: "2 4" }, selectable: false, deletable: false,
    markerEnd: { type: MarkerType.ArrowClosed, color: stroke },
    zIndex: 0,
  };
}

type Funding = { funded: number; needed: number; live: boolean };

function FlowInner({
  graph, ledgers, update, onLaunchClick, onCreateWallet, validation, funding, undo, redo,
  autoMode, setAutoMode
}: {
  graph: FlowGraph;
  ledgers: Map<string, WalletLedger>;
  update: (fn: (draft: FlowGraph) => void) => void;
  onLaunchClick: () => void;
  onCreateWallet: (x: number, y: number) => void;
  validation: Validation;
  funding: Funding;
  undo: () => void;
  redo: () => void;
  autoMode: boolean;
  setAutoMode: (v: boolean) => void;
}) {
  const [selectedEdges, setSelectedEdges] = useState<string[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const clipboard = useRef<string[]>([]); // copied wallet ids
  const [inspectorId, setInspectorId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; fx: number; fy: number } | null>(null);
  const [connectErr, setConnectErr] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const { screenToFlowPosition } = useReactFlow();
  const [issuesCollapsed, setIssuesCollapsed] = useState(false);

  const blocks = validation.issues.filter((i) => i.severity === "block").length;
  const warns = validation.issues.filter((i) => i.severity === "warn").length;

  const warn = (msg: string) => { setConnectErr(msg); window.setTimeout(() => setConnectErr(""), 2800); };

  useEffect(() => {
    setNodes((cur) => {
      const curMap = new Map(cur.map((n) => [n.id, n]));
      
      // The creator/dev wallet IS the launch node — render it as the black DEV box,
      // not a duplicate card.
      const creatorId = resolveDevId(graph);
      const creator = graph.wallets.find((w) => w.id === creatorId);
      
      const launchNode: Node = {
        ...(curMap.get(LAUNCH_NODE_ID) || { id: LAUNCH_NODE_ID, type: "launch", position: LAUNCH_POS }),
        data: { devLabel: creator?.label ?? "Wallet 1", devBuy: graph.devBuySol, ledger: creatorId ? ledgers.get(creatorId) : undefined } satisfies LaunchNodeData,
      };

      const rotTargets = new Set(graph.edges.filter((e) => e.kind === "rotate").map((e) => e.target));
      const labels = walletLabels(graph);
      
      const walletNodes: Node[] = graph.wallets.filter((w) => w.id !== creatorId).map((w) => {
        const existing = curMap.get(w.id);
        const inEdge = graph.edges.find(e => e.target === w.id && e.kind === "buy");
        return {
          ...(existing || { id: w.id, type: "wallet", position: { x: w.x, y: w.y } }),
          data: { 
            label: w.label, 
            group: w.group, 
            enabled: w.enabled, 
            inRotation: rotTargets.has(w.id), 
            orderLabel: labels.get(w.id), 
            ledger: ledgers.get(w.id),
            initBuy: inEdge?.sol,
            delay: inEdge?.waitSec
          } satisfies WalletNodeData,
        };
      });

      const sellNodes: Node[] = graph.sells.map((s) => {
        const existing = curMap.get(s.id);
        return {
          ...(existing || { id: s.id, type: "sell", position: { x: s.x, y: s.y } }),
          data: { value: s.value, pct: s.pct, severity: validation.sell.get(s.id) ?? "ok" } satisfies SellNodeData,
        };
      });

      return [launchNode, ...walletNodes, ...sellNodes];
    });
  }, [graph.wallets, graph.edges, graph.sells, graph.launch.devWalletId, graph.devBuySol, ledgers, validation, setNodes]);

  useEffect(() => {
    const main = graph.edges.map((e) => edgeToRf(e, validation.edge.get(e.id) ?? "ok", e.id === inspectorId));
    const sells = graph.sells.map((s) => sellEdge(s, validation.sell.get(s.id) ?? "ok"));
    setEdges([...main, ...sells]);
  }, [graph.edges, graph.sells, validation, inspectorId, setEdges]);

  const onConnect = useCallback((c: Connection) => {
    if (!c.source || !c.target) return;
    // the connector you dragged from is the action: green B = buy, red S = rotate (sell into a wallet)
    const kind: EdgeKind = c.sourceHandle === "sell" ? "rotate" : "buy";
    const check = canConnect(graph, c.source, c.target, kind);
    if (!check.ok) return warn(check.reason || "Can't connect those");
    update((d) => {
      const order = d.edges.length ? Math.max(...d.edges.map((e) => e.order)) + 1 : 0;
      d.edges.push(defaultEdge(kind, c.source!, c.target!, order));
    });
  }, [update, graph]);

  // drag a wallet's handle to EMPTY canvas → create a timed sell on that wallet
  const onConnectEnd = useCallback((event: MouseEvent | TouchEvent, state: FinalConnectionState) => {
    if (state.isValid) return; // landed on a node → handled by onConnect
    if (state.fromHandle?.id !== "sell" || !state.fromNode) return;
    // Launch's sell handle sells the DEV/creator wallet's bag (it holds the dev buy)
    const walletId = state.fromNode.id === LAUNCH_NODE_ID
      ? (graph.launch.devWalletId || graph.wallets[0]?.id || "")
      : state.fromNode.id;
    if (!walletId) return warn("No creator wallet to sell — add a wallet first");
    const ok = canSell(graph, walletId);
    if (!ok.ok) return warn(state.fromNode.id === LAUNCH_NODE_ID ? `Creator wallet already has a sell/rotate` : (ok.reason || "Can't add a sell here"));
    const touch = "clientX" in event ? null : event.changedTouches?.[0];
    if (!("clientX" in event) && !touch) return; // touch-cancel with no point
    const pt = "clientX" in event ? { x: event.clientX, y: event.clientY } : { x: touch!.clientX, y: touch!.clientY };
    const p = screenToFlowPosition(pt);
    const s = defaultSell(walletId, p.x, p.y);
    update((d) => d.sells.push(s));
    setInspectorId(s.id);
  }, [update, graph, screenToFlowPosition]);

  const deleteEdge = useCallback((id: string) => {
    update((d) => { d.edges = d.edges.filter((e) => e.id !== id); });
    setInspectorId((cur) => (cur === id ? null : cur));
  }, [update]);
  const deleteSell = useCallback((id: string) => {
    update((d) => { d.sells = d.sells.filter((s) => s.id !== id); });
    setInspectorId((cur) => (cur === id ? null : cur));
  }, [update]);

  const onReconnect = useCallback((oldEdge: Edge, c: Connection) => {
    if (!c.source || !c.target) return;
    const e0 = graph.edges.find((x) => x.id === oldEdge.id);
    if (!e0) return;
    const check = canConnect({ ...graph, edges: graph.edges.filter((x) => x.id !== oldEdge.id) }, c.source, c.target, e0.kind);
    if (!check.ok) return warn(check.reason || "Can't move it there");
    update((d) => { const e = d.edges.find((x) => x.id === oldEdge.id); if (e) { e.source = c.source!; e.target = c.target!; } });
  }, [update, graph]);

  const onNodeDragStop = useCallback((_: unknown, node: Node) => {
    if (node.id === LAUNCH_NODE_ID) return;
    update((d) => {
      const w = d.wallets.find((x) => x.id === node.id);
      if (w) { w.x = node.position.x; w.y = node.position.y; return; }
      const s = d.sells.find((x) => x.id === node.id);
      if (s) { s.x = node.position.x; s.y = node.position.y; }
    });
  }, [update]);

  const onSelectionChange = useCallback((p: OnSelectionChangeParams) => {
    setSelectedEdges(p.edges.map((e) => e.id));
    setSelectedNodes(p.nodes.map((n) => n.id).filter((id) => id !== LAUNCH_NODE_ID));
  }, []);

  function bundleSelected() {
    const selIds = new Set<string>(selectedEdges);
    selectedNodes.forEach(nid => {
      const edge = graph.edges.find(e => e.target === nid);
      if (edge) selIds.add(edge.id);
    });
    const edgeIds = Array.from(selIds);
    const edgesToBundle = graph.edges.filter(e => edgeIds.includes(e.id));
    if (edgesToBundle.length < 2) return warn("Select at least 2 consecutive wallets/steps to bundle");
    
    const idx = edgesToBundle.map(e => e.order).sort((a, b) => a - b);
    if (!idx.every((v, k) => k === 0 || v === idx[k - 1] + 1)) return warn("Bundle only consecutive steps");
    
    const bid = `bundle-${idx[0]}`;
    update((d) => d.edges.forEach((e) => { if (selIds.has(e.id)) e.bundleId = bid; }));
  }

  const ungroup = () => update((d) => {
    const selIds = new Set<string>(selectedEdges);
    selectedNodes.forEach(nid => {
      const edge = graph.edges.find(e => e.target === nid);
      if (edge) selIds.add(edge.id);
    });
    d.edges.forEach((e) => { if (selIds.has(e.id)) delete e.bundleId; });
  });

  // delete everything selected: wallet cards (+ their edges & sells), sell cards, and edges
  const deleteSelection = useCallback(() => {
    const wIds = new Set(selectedNodes.filter((id) => graph.wallets.some((w) => w.id === id)));
    const sIds = new Set(selectedNodes.filter((id) => graph.sells.some((s) => s.id === id)));
    const eIds = new Set(selectedEdges);
    if (!wIds.size && !sIds.size && !eIds.size) return;
    update((d) => {
      d.wallets = d.wallets.filter((w) => !wIds.has(w.id));
      d.edges = d.edges.filter((e) => !eIds.has(e.id) && !wIds.has(e.source) && !wIds.has(e.target));
      d.sells = d.sells.filter((s) => !sIds.has(s.id) && !wIds.has(s.walletId));
    });
    setSelectedEdges([]); setSelectedNodes([]); setInspectorId(null);
  }, [selectedNodes, selectedEdges, graph, update]);

  // copy selected wallets → paste as fresh wallets (new ids), offset, no edges
  const copySelection = useCallback(() => { clipboard.current = selectedNodes.filter((id) => graph.wallets.some((w) => w.id === id)); }, [selectedNodes, graph]);
  const pasteSelection = useCallback(() => {
    const src = clipboard.current.map((id) => graph.wallets.find((w) => w.id === id)).filter(Boolean) as FlowGraph["wallets"];
    if (!src.length) return;
    update((d) => { for (const w of src) d.wallets.push({ ...w, id: uid("w"), label: `${w.label} copy`, x: (w.x ?? 0) + 40, y: (w.y ?? 0) + 40 }); });
  }, [graph, update]);
  const selectAll = useCallback(() => setNodes((ns) => ns.map((n) => (n.id === LAUNCH_NODE_ID ? n : { ...n, selected: true }))), [setNodes]);

  const del = deleteSelection;

  // Figma-style keyboard shortcuts (ignored while typing in a field)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      const meta = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSelection(); }
      else if (meta && k === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (meta && k === "y") { e.preventDefault(); redo(); }
      else if (meta && k === "a") { e.preventDefault(); selectAll(); }
      else if (meta && k === "c") copySelection();
      else if (meta && k === "v") { e.preventDefault(); pasteSelection(); }
      else if (meta && k === "d") { e.preventDefault(); copySelection(); pasteSelection(); } // duplicate in place
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteSelection, copySelection, pasteSelection, selectAll, undo, redo]);

  const selEdge = inspectorId ? graph.edges.find((e) => e.id === inspectorId) ?? null : null;
  const selSell = inspectorId ? graph.sells.find((s) => s.id === inspectorId) ?? null : null;

  return (
    <div className="panel" style={{ flex: 1 }}>
      <div className="panel-head">
        <h2>Playground · {graph.edges.length} steps{blocks ? ` · ${blocks} blocked` : warns ? ` · ${warns} warnings` : graph.edges.length ? " · valid" : ""}</h2>
        <div className="row">
          {funding.live && (
            <span className={`live-tag ${funding.funded + 1e-9 >= funding.needed ? "on" : ""}`}
              title="SOL in wallets / SOL this flow needs to run"
              style={funding.funded + 1e-9 >= funding.needed ? {} : { color: "#14b8a6", background: "rgba(45,212,191,0.16)" }}>
              {funding.funded.toFixed(2)}/{funding.needed.toFixed(2)} SOL
            </span>
          )}
          {blocks > 0 && <span className="live-tag off">⛔ {blocks}</span>}
          {blocks === 0 && warns > 0 && <span className="live-tag" style={{ color: "#14b8a6", background: "rgba(45,212,191,0.16)" }}>⚠ {warns}</span>}
          {graph.edges.length > 0 && blocks === 0 && warns === 0 && <span className="live-tag on">✓ valid</span>}
          <div className="pg-controls-right z-50 pointer-events-auto flex flex-wrap items-center gap-1">
            <button className="btn btn-sm btn-ghost" onClick={undo} title="Undo">↶</button>
            <button className="btn btn-sm btn-ghost" onClick={redo} title="Redo">↷</button>
            <div className="w-[1px] h-4 bg-white/20 mx-1"></div>
            <button className={`btn btn-sm flex items-center gap-1.5 ${autoMode ? "btn-gold active" : "btn-ghost"}`} onClick={() => setAutoMode(!autoMode)} title="Auto Setup Mode">
              <Zap size={14} /> Auto Setup
            </button>
            <button className="btn btn-sm btn-ghost items-center gap-1.5 hidden sm:flex" disabled={selectedEdges.length + selectedNodes.length < 2} onClick={bundleSelected} title="Bundle selected wallets">
              <Boxes size={14} /> Bundle
            </button>
            <button className="btn btn-sm btn-ghost items-center gap-1.5 hidden sm:flex" disabled={selectedEdges.length + selectedNodes.length === 0} onClick={ungroup} title="Ungroup selected bundle">
              <Ungroup size={14} /> Ungroup
            </button>
            <button 
              onClick={onLaunchClick} 
              disabled={!validation.ok}
              className="sm:hidden bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/10 disabled:text-white/30 text-black px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 ml-1 shadow"
            >
              <Play fill="currentColor" size={10} /> Launch
            </button>
          </div>

          <div className="hidden sm:flex absolute top-6 left-1/2 -translate-x-1/2 z-50 pointer-events-auto flex-col gap-2 w-full max-w-lg">
            <div className="flex items-center gap-4 bg-black/80 backdrop-blur border border-white/10 rounded-full px-6 py-3 shadow-2xl mx-auto">
              <div className="flex flex-col">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Playground State</div>
                <div className="text-sm font-black text-white">{validation.ok ? "Ready for Launch" : `${blocks} Errors, ${warns} Warnings`}</div>
              </div>
              <div className="w-[1px] h-8 bg-white/20 mx-2"></div>
              <button 
                onClick={onLaunchClick} 
                disabled={!validation.ok}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/10 disabled:text-white/30 text-black px-8 py-3 rounded-full text-sm font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] disabled:shadow-none"
              >
                <Play fill="currentColor" size={16} /> Execute Launch
              </button>
            </div>
            
            {!validation.ok && validation.issues.length > 0 && (
              <div className="bg-black/90 backdrop-blur border border-red-500/30 rounded-xl p-3 shadow-xl flex flex-col gap-1.5 animate-in fade-in slide-in-from-top-2 mx-auto w-full">
                <div 
                  className="flex items-center justify-between cursor-pointer px-1 group"
                  onClick={() => setIssuesCollapsed(!issuesCollapsed)}
                >
                  <div className="text-[10px] font-black uppercase tracking-widest text-red-400">Fix the following issues ({validation.issues.length}):</div>
                  <div className="text-white/40 group-hover:text-white/80 transition-colors">
                    {issuesCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                  </div>
                </div>
                {!issuesCollapsed && (
                  <div className="max-h-48 overflow-y-auto custom-scrollbar flex flex-col gap-1.5 mt-1">
                    {validation.issues.map((issue, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs bg-white/5 rounded-lg p-2">
                        <span className={`shrink-0 mt-0.5 ${issue.severity === "block" ? "text-red-500" : "text-amber-500"}`}>
                          {issue.severity === "block" ? "⛔" : "⚠"}
                        </span>
                        <span className="text-white/90 leading-relaxed">{issue.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <ReactFlow
          nodes={nodes} edges={edges} nodeTypes={nodeTypes}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          onConnect={onConnect} onConnectEnd={onConnectEnd} onReconnect={onReconnect}
          onNodeDragStop={onNodeDragStop} onSelectionChange={onSelectionChange}
          onEdgeClick={(_, edge) => setInspectorId(edge.id)}
          onNodeClick={(_, node) => {
            if (node.id === LAUNCH_NODE_ID) onLaunchClick();
            else if (node.type === "sell") setInspectorId(node.id);
            else {
              // clicking a wallet card = clicking its incoming buy tag → open that edge's inspector
              const inEdge = graph.edges.find((e) => e.kind === "buy" && e.target === node.id) ?? graph.edges.find((e) => e.target === node.id);
              setInspectorId(inEdge ? inEdge.id : null);
            }
          }}
          onPaneClick={() => { setInspectorId(null); setMenu(null); }}
          onMoveStart={() => setMenu(null)}
          onPaneContextMenu={(e) => {
            e.preventDefault();
            const me = e as MouseEvent;
            const p = screenToFlowPosition({ x: me.clientX, y: me.clientY });
            setMenu({ x: me.clientX, y: me.clientY, fx: p.x, fy: p.y });
          }}
          fitView minZoom={0.3} maxZoom={2.5} nodeDragThreshold={1} elevateNodesOnSelect
          selectionOnDrag panOnDrag={[1, 2]} selectionMode={SelectionMode.Partial} deleteKeyCode={null}
          proOptions={{ hideAttribution: true }} defaultEdgeOptions={{ type: "bezier" }}
        >
          <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="rgba(255,255,255,0.06)" />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable nodeColor={(n) => (n.id === LAUNCH_NODE_ID ? "#000000" : n.type === "sell" ? "#ef4444" : "#10b981")} nodeStrokeWidth={2} maskColor="rgba(0,0,0,0.6)" />
        </ReactFlow>
        
        {autoMode && (
          <PlaygroundConfigurator 
            graph={graph} 
            update={update} 
            onClose={() => setAutoMode(false)}
          />
        )}

        {menu && (
          <>
            <div className="pg-ctx-backdrop" onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} />
            <div className="pg-ctx-menu" style={{ left: menu.x, top: menu.y }}>
              <button onClick={() => { onCreateWallet(menu.fx, menu.fy); setMenu(null); }}><Plus size={13} /> Create wallet</button>
            </div>
          </>
        )}
        {connectErr && <div className="connect-toast">⛔ {connectErr}</div>}
        {selEdge && <EdgeInspector edge={selEdge} graph={graph} update={update} messages={validation.edgeMsg.get(selEdge.id) ?? []} onClose={() => setInspectorId(null)} onDelete={() => deleteEdge(selEdge.id)} />}
        {selSell && <SellInspector sell={selSell} graph={graph} update={update} messages={validation.sellMsg.get(selSell.id) ?? []} onClose={() => setInspectorId(null)} onDelete={() => deleteSell(selSell.id)} />}
        {graph.edges.length === 0 && (
          <div className="pg-hint">Drag a handle <b>Launch → wallet</b> to start, then <b>wallet → wallet</b> to chain. Drag to <b>empty space</b> for a sell. <b>Drag on canvas</b> to box-select · <b>Del</b> removes · <b>⌘Z</b> undo · <b>⌘C/⌘V</b> copy/paste · middle-drag or scroll to pan.</div>
        )}
        <div className="pg-legend">
          <span><i style={{ background: BUY_COLOR }} /> buy</span>
          <span><i className="dash" style={{ background: ROTATE_COLOR }} /> rotate</span>
          <span><i className="dash" style={{ background: SELL_COLOR }} /> sell</span>
        </div>
      </div>
    </div>
  );
}

export function Playground(props: {
  graph: FlowGraph;
  ledgers: Map<string, WalletLedger>;
  update: (fn: (draft: FlowGraph) => void) => void;
  onLaunchClick: () => void;
  onCreateWallet: (x: number, y: number) => void;
  validation: Validation;
  funding: Funding;
  undo: () => void;
  redo: () => void;
}) {
  const [autoMode, setAutoMode] = useState(true);

  return (
    <ReactFlowProvider>
      <FlowInner {...props} autoMode={autoMode} setAutoMode={setAutoMode} />
    </ReactFlowProvider>
  );
}

function EdgeInspector({
  edge, graph, update, messages, onClose, onDelete,
}: {
  edge: GraphEdge;
  graph: FlowGraph;
  update: (fn: (draft: FlowGraph) => void) => void;
  messages: string[];
  onClose: () => void;
  onDelete: () => void;
}) {
  const patch = (fn: (e: GraphEdge) => void) => update((d) => { const e = d.edges.find((x) => x.id === edge.id); if (e) fn(e); });
  const name = (id: string) => (id === LAUNCH_NODE_ID ? "Launch" : graph.wallets.find((w) => w.id === id)?.label ?? id);
  return (
    <div className="pg-inspector panel">
      <div className="panel-head">
        <h2>{edge.kind === "buy" ? "Buy" : "Rotate"} · {name(edge.source)} → {name(edge.target)}</h2>
        <button className="icon-btn" title="Close" onClick={onClose}><X size={15} /></button>
      </div>
      <div className="panel-body">
        {messages.length > 0 && <div className="edge-issues">{messages.map((m, i) => <div key={i}>⛔ {m}</div>)}</div>}
        {edge.kind === "buy" ? (
          <div className="row">
            <div style={{ flex: 1 }}>
              <label className="lbl">Buy amount (SOL)</label>
              <input className="field" type="number" min="0" step="0.01" value={edge.sol} onChange={(e) => patch((x) => { x.sol = Math.max(0, Number(e.target.value) || 0); })} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="lbl">{edge.bundleId ? "Wait (bundled)" : "Wait (seconds)"}</label>
              <input className="field" type="number" min="0" step="1" disabled={!!edge.bundleId} title={edge.bundleId ? "Bundled buys fire together in one slot" : "Seconds to wait before this buy fires"} value={edge.bundleId ? 0 : edge.waitSec ?? 2} onChange={(e) => patch((x) => { x.waitSec = Math.max(0, Number(e.target.value) || 0); })} />
            </div>
          </div>
        ) : (
          <div className="small muted" style={{ lineHeight: 1.5 }}>
            <b style={{ color: ROTATE_COLOR }}>Rotate (instant)</b> — {name(edge.source)} sells its full position, the SOL is swept to {name(edge.target)}, and {name(edge.target)} buys back immediately. No settings.
          </div>
        )}
        <div className="row" style={{ marginTop: 8, justifyContent: "space-between" }}>
          <span className="small muted">drag a line end to move it</span>
          <button className="btn btn-sm btn-danger btn-ghost" onClick={onDelete}><Trash2 size={12} /> Delete</button>
        </div>
      </div>
    </div>
  );
}

function SellInspector({
  sell, graph, update, messages, onClose, onDelete,
}: {
  sell: SellNode;
  graph: FlowGraph;
  update: (fn: (draft: FlowGraph) => void) => void;
  messages: string[];
  onClose: () => void;
  onDelete: () => void;
}) {
  const patch = (fn: (s: SellNode) => void) => update((d) => { const s = d.sells.find((x) => x.id === sell.id); if (s) fn(s); });
  const wallet = graph.wallets.find((w) => w.id === sell.walletId)?.label ?? "wallet";
  return (
    <div className="pg-inspector panel">
      <div className="panel-head">
        <h2>Sell · {wallet}</h2>
        <button className="icon-btn" title="Close" onClick={onClose}><X size={15} /></button>
      </div>
      <div className="panel-body">
        {messages.length > 0 && <div className="edge-issues">{messages.map((m, i) => <div key={i}>⛔ {m}</div>)}</div>}
        <label className="lbl">Sell Strategy</label>
        <div className="row" style={{ marginTop: 6, marginBottom: 8 }}>
          <select 
            className="field w-full" 
            value={sell.trigger === "loop" ? "loop" : "time"} 
            onChange={(e) => patch((s) => { s.trigger = e.target.value as any; })}
          >
            <option value="time">One-time Sell (Delay)</option>
            <option value="loop">Continuous Loop (Bot)</option>
          </select>
        </div>
        <div className="row" style={{ marginTop: 6 }}>
          <div style={{ flex: 1 }}>
            <label className="lbl">{sell.trigger === "loop" ? "Interval (seconds)" : "Delay (seconds)"}</label>
            <input className="field" type="number" min="0" step="5" value={sell.value}
              onChange={(e) => patch((s) => { s.value = Math.max(0, Number(e.target.value) || 0); })} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="lbl">Sell % of holdings</label>
            <input className="field" type="number" min="1" max="100" step="5" value={sell.pct} onChange={(e) => patch((s) => { s.pct = Math.max(0, Math.min(100, Number(e.target.value) || 0)); })} />
          </div>
        </div>
        <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm btn-danger btn-ghost" onClick={onDelete}><Trash2 size={12} /> Delete sell</button>
        </div>
      </div>
    </div>
  );
}

function compact(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
}
